import { ROUTE_MIN_POINTS } from "src/domains/shared/utils/equipment/equipmentRoute";
// 타입만 — 런타임에 지도 엔진 상수를 끌어오지 않는다(2D 칩 번들 보호)
import type { MeasurementAxisLabelKey } from "src/domains/shared/constants/measurementAxisLabels";
import {
  DRAW_MIN_POINTS,
  DrawToolKind,
  finishesByDoubleClick,
  finishesOnFirstClick,
  finishesOnSecondClick,
} from "./annotationDrawSession";

/** 안내 칩 한 줄 = 버튼 하나 — 왼쪽 버튼, 오른쪽 그 결과 (annotation-tool-interaction 스펙) */
export type ToolGuideButton =
  | "click"
  | "doubleClick"
  | "rightClick"
  | "release"
  | "drag"
  | "clickPlus"
  | "rightClickPoint";
export type ToolGuideAction =
  | "createPoint"
  | "finish"
  | "cancel"
  | "undo"
  | "removeMeasurement"
  | "nearbyImage"
  | "placeEquipment"
  | "movePoint"
  | "removePoint"
  | "appendPoint";

export interface ToolGuideRow {
  button: ToolGuideButton;
  action: ToolGuideAction;
}

/** 측정 축 — 3D `MEASUREMENT_STYLES` 의 키와 같다. 칩은 축 이름으로 아이콘 모양을 고른다 */
export type ToolGuideReadoutAxis = "solid" | "plane" | "guide";

export interface ToolGuideReadoutRow {
  axis: ToolGuideReadoutAxis;
  /** `common` 네임스페이스의 축 이름 키 */
  labelKey: MeasurementAxisLabelKey;
  /** 축 색(아이콘·축 이름) — 칩이 지도 엔진 상수를 import 하지 않도록 값으로 받는다 */
  color: string;
}

/**
 * 안내 칩의 **실시간 측정값** 슬롯(spec inspection3d-tool-guide 「안내 칩은 측정 도구의 실시간 값을 함께 보여준다」).
 * 값 갱신은 React 를 거치지 않는다 — 칩은 줄 DOM 을 `bind` 로 넘기고, 도구 쪽 readout 객체가 textContent 를
 * 명령형으로 쓴다(이동 빈도 경로). `container` 는 줄 전체를 감싸는 블록(값이 하나도 없으면 숨긴다).
 */
export interface ToolGuideReadout {
  rows: ToolGuideReadoutRow[];
  bind(target: ToolGuideReadoutAxis | "container", element: HTMLElement | null): void;
}

/**
 * `equipmentClick` = 모달에서 시작한 장비 배치(클릭으로 놓기), `equipmentDrop` = 카드 끌기(버튼을 놓아 놓기),
 * `equipmentRouteDraw`·`equipmentRouteEdit` = 장비 동선 그리기·수정
 */
export type ToolGuideKind =
  | DrawToolKind
  | "remover"
  | "droneImage"
  | "equipmentClick"
  | "equipmentDrop"
  | "equipmentRouteDraw"
  | "equipmentRouteEdit";

export const TOOL_GUIDE_BUTTON_KEYS = {
  click: "mapTool.guide.button.click",
  doubleClick: "mapTool.guide.button.doubleClick",
  rightClick: "mapTool.guide.button.rightClick",
  release: "mapTool.guide.button.release",
  drag: "mapTool.guide.button.drag",
  clickPlus: "mapTool.guide.button.clickPlus",
  rightClickPoint: "mapTool.guide.button.rightClickPoint",
} as const satisfies Record<ToolGuideButton, string>;

export const TOOL_GUIDE_ACTION_KEYS = {
  createPoint: "mapTool.guide.action.createPoint",
  finish: "mapTool.guide.action.finish",
  cancel: "mapTool.guide.action.cancel",
  undo: "mapTool.guide.action.undo",
  removeMeasurement: "mapTool.guide.action.removeMeasurement",
  nearbyImage: "mapTool.guide.action.nearbyImage",
  placeEquipment: "mapTool.guide.action.placeEquipment",
  movePoint: "mapTool.guide.action.movePoint",
  removePoint: "mapTool.guide.action.removePoint",
  appendPoint: "mapTool.guide.action.appendPoint",
} as const satisfies Record<ToolGuideAction, string>;

export const TOOL_GUIDE_PRECISE_PENDING_KEY = "mapTool.guide.precisePending";

const row = (button: ToolGuideButton, action: ToolGuideAction): ToolGuideRow => ({
  button,
  action,
});

/**
 * 도구 종류·`count` → 칩 줄 목록. 줄 순서는 클릭 → 더블클릭 → 우클릭.
 * `count` 의 뜻은 `kind` 별로 다르다 — 그리기 도구(`DrawToolKind`)는 확정 점 개수, `equipmentRouteDraw` 는 찍은 점
 * 개수(첫 점인 장비 위치는 빼고), `equipmentRouteEdit` 는 되돌릴 수 있는 변경 수이고, `remover`·`droneImage`·
 * `equipmentClick`·`equipmentDrop` 은 읽지 않는다.
 * 더블클릭의 구성 클릭이 마지막 점을 겸하므로 "더블클릭 — 완료" 는 최소 점 -1 부터 보인다.
 */
export function resolveToolGuideRows(
  kind: ToolGuideKind,
  count: number
): ToolGuideRow[] {
  if (kind === "remover") {
    return [row("click", "removeMeasurement"), row("rightClick", "cancel")];
  }
  if (kind === "droneImage") {
    return [row("click", "nearbyImage"), row("rightClick", "cancel")];
  }
  if (kind === "equipmentClick") {
    return [row("click", "placeEquipment"), row("rightClick", "cancel")];
  }
  if (kind === "equipmentDrop") {
    return [row("release", "placeEquipment"), row("rightClick", "cancel")];
  }
  if (kind === "equipmentRouteEdit") {
    return [
      row("drag", "movePoint"),
      row("clickPlus", "createPoint"),
      row("click", "appendPoint"),
      row("doubleClick", "finish"),
      row("rightClickPoint", "removePoint"),
      // 되돌릴 변경이 없으면 우클릭이 수정을 끝낸다
      row("rightClick", count > 0 ? "undo" : "cancel"),
    ];
  }
  if (kind === "equipmentRouteDraw") {
    // 완료 줄은 측정 도구와 같이 최소 점 -1 부터
    const rows = [row("click", "createPoint")];
    if (count > 0 && count >= ROUTE_MIN_POINTS - 1)
      rows.push(row("doubleClick", "finish"));
    rows.push(row("rightClick", count > 0 ? "undo" : "cancel"));
    return rows;
  }
  // 완료 손짓 분류는 좌클릭 판정과 같은 표(`DRAW_FINISH_MODE`)를 읽는다
  if (finishesOnFirstClick(kind)) {
    return [row("click", "createPoint"), row("rightClick", "cancel")];
  }

  const rightClick = row("rightClick", count > 0 ? "undo" : "cancel");
  // 사각형과 수직 측정은 둘 다 두 번째 좌클릭이 완료라 같은 줄 목록을 쓴다
  if (finishesOnSecondClick(kind)) {
    return [row("click", count > 0 ? "finish" : "createPoint"), rightClick];
  }

  const rows = [row("click", "createPoint")];
  // 여기 도달하는 kind 는 선·면뿐이라 `finishesByDoubleClick` 은 방어적 가드다 — 판정은 손짓 규칙과 같은 표를 읽는다
  const showsDoubleClickFinishRow =
    finishesByDoubleClick(kind) &&
    count > 0 &&
    count >= DRAW_MIN_POINTS[kind] - 1;
  if (showsDoubleClickFinishRow) {
    rows.push(row("doubleClick", "finish"));
  }
  rows.push(rightClick);
  return rows;
}
